<template>
  <div>
    <service-rent-calendar-header
      :enable-reservation="enableReservationButton"
      @reserve="$emit('reserve')"
      @cancel="$emit('cancel')"
    />
    <loading-spinner v-if="loadingCalendar" />
    <full-calendar
      v-else
      ref="calendar"
      class="calendar"
      :options="calendarOptions"
    />
  </div>
</template>

<script>
import moment from "moment";
import ServiceRentCalendarHeader from "./ServiceRentCalendarHeader";

import "@fullcalendar/core/vdom";
import timeGridPlugin from "@fullcalendar/timegrid";
import momentPlugin from "@fullcalendar/moment";
import interactionPlugin from "@fullcalendar/interaction";
import _cloneDeep from "lodash/cloneDeep";

/* Lazy loaded components */
const FullCalendar = () => import("@fullcalendar/vue");

export default {
  name: "ServiceRentCalendar",

  components: {
    FullCalendar,
    ServiceRentCalendarHeader
  },

  props: {
    events: {
      type: Array,
      default: () => []
    },
    header: {
      type: Object,
      default() {
        return {
          left: "prev,title,next",
          center: "",
          right: ""
        };
      }
    },
    defaultView: {
      type: String,
      default() {
        return "timeGridWeek";
      }
    },
    modalOpen: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      calendar: null,
      config: {
        allDaySlot: false,
        columnHeaderHtml: m => this.customCalendarHeader(m),
        defaultDate: moment(),
        defaultTimedEventDuration: "03:00:00",
        defaultView: "agendaWeek",
        eventBackgroundColor: "#013B89",
        eventBorderColor: "#013B89",
        eventTextColor: "#ffff",
        header: false,
        height: "auto",
        firstDay: 1,
        forceEventDuration: true,
        locale: "es",
        slotDuration: "00:30:00",
        slotLabelFormat: ["h A"]
      },
      requestPanelProps: {
        show: false,
        left: 0,
        top: 0
      },
      loadingCalendar: true
    };
  },

  computed: {
    calendarOptions() {
      const options = {
        plugins: [timeGridPlugin, momentPlugin, interactionPlugin],
        initialView: this.defaultView,
        slotDuration: "01:00:00", //Duration of a row
        eventMinHeight: 120, //Minimum time for an event
        slotMinTime: "00:00:00", //First hour of the day
        slotMaxTime: "24:00:00", //Last hour of the day
        allDaySlot: false, //Disable a allDay row
        firstDay: 1, //Set Monday as First Day
        titleFormat: "D / MMMM",
        slotLabelFormat: "h A",
        eventColor: "#6F6F6F",
        defaultTimedEventDuration: "04:00",
        height: "auto",
        headerToolbar: this.header,
        locale: "esLocale",
        editable: this.modalOpen ? false : true,
        selectable: this.modalOpen ? false : true,
        eventTimeFormat: {
          hour: "numeric",
          minute: "2-digit",
          meridiem: "short"
        },
        events: this.calendarEvents,
        eventOverlap: false,
        eventContent: arg => {
          const title = !arg.event.extendedProps.address?.address
            ? "Ingresa Origen"
            : arg.event.extendedProps.address.address;
          const drivers = !arg.event.extendedProps.drivers
            ? 1
            : arg.event.extendedProps.drivers;

          return {
            html:
              "<div> <div class='font-weight-bold'>" +
              title +
              "</div><div>" +
              moment(arg.event.start).format("h:mma") +
              " - " +
              moment(arg.event.end).format("h:mma") +
              "</div><div class='pt-3'> " +
              `<img src="${require("@/assets/img/driver-white.png")}">` +
              " " +
              drivers +
              "</div></div>"
          };
        },

        select: (event, view) => {
          this.$emit("event-render", event, view);
        },

        eventClick: arg => {
          this.$emit("event-selected", arg.event, arg.el);
        },

        eventDrop: arg => {
          this.$emit("event-drop", arg.event, arg.delta, arg.revert);
        },

        eventResize: arg => {
          this.$emit("event-resize", arg.event, arg.delta, arg.revert);
        },

        //Change days header
        dayHeaderContent: arg => {
          return {
            html: `
            <div class='py-3 d-flex flex-column align-items-center'>
                <div>${moment(arg.date).format("D")}</div>
                <div class='day-name'>${moment(arg.date)
                  .format("dddd")
                  .toUpperCase()
                  .replace(".", "")}</div>
            </div>`
          };
        },
        //Styles
        slotLaneClassNames: "table-cells",
        slotLabelClassNames: "border-0 slot-labels"
      };
      return options;
    },

    calendarEvents() {
      return _cloneDeep(this.events);
    },

    eventsPendingToReserve() {
      return this.events.filter(ev => ev.saved && !ev.reserved);
    },
    enableReservationButton() {
      return this.eventsPendingToReserve.length > 0;
    },
    calendarRef() {
      return this.$refs.calendar;
    }
  },

  mounted() {
    FullCalendar().finally(() => {
      this.loadingCalendar = false;
    });
  }
};
</script>

<style scoped lang="scss">
::v-deep .fc-scrollgrid {
  border-radius: 10px;
  overflow: hidden;
}
::v-deep .fc-toolbar-chunk {
  div:first-child {
    display: flex;
  }
}

::v-deep .fc-timegrid-axis {
  border: none !important;
}

::v-deep .fc-button-primary {
  background-color: $dodger-blue;
  height: 32px;
  border-radius: 0px;
  width: 31px;
  border: none;

  &:hover,
  &:focus,
  &:active {
    background-color: $dodger-blue !important;
    box-shadow: none !important;
  }

  &:disabled {
    background-color: $solitude;

    .fc-icon-chevron-left {
      color: $comet !important;
    }

    &:hover,
    &:focus,
    &:active {
      background-color: $solitude !important;
    }
  }
  .fc-icon-chevron-left,
  .fc-icon-chevron-right {
    transform: translate(-3px, -1px);
  }
}

::v-deep .fc-prev-button {
  border-radius: 6px 0 0 6px;
}

::v-deep .fc-next-button {
  border-radius: 0 6px 6px 0;
}

::v-deep .fc-icon {
  transform: translate(-8px, 0);
}

::v-deep .fc-toolbar-title {
  font-family: Montserrat;
  font-size: 12px;
  border: 1px solid #d4d7df;
  height: 32px;
  width: 220px !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

::v-deep .slot-labels {
  color: #656b7f;
  font-family: Montserrat;
  font-size: 8px;
  transform: translate(0, -5px);
  width: 56px !important;
}

::v-deep .fc-day-disabled {
  background-color: white;
}

::v-deep .day-name {
  color: black;
  font-size: 10px;

  &:hover {
    text-decoration: none;
  }
}

::v-deep .fc-day-today {
  background-color: white;
}

::v-deep .fc .fc-timegrid-col.fc-day-today {
  background-color: white !important;
}

::v-deep .fc-timegrid-col {
  &.fc-day {
    border: 1px solid #d4d7df;
    height: calc(34px * 24);
  }
}

::v-deep .fc-col-header-cell {
  &.fc-day-today {
    .flex-column div:first-child {
      color: $dodger-blue !important;
      position: relative;
      height: 20px;
      width: 20px;
      border-radius: 6px;
    }

    .day-name {
      top: -5px !important;
      color: $dodger-blue !important;
    }
  }
}

::v-deep .table-cells {
  height: 34px;
}
::v-deep .fc-col-header-cell-cushion {
  color: black;

  &:hover {
    color: black;
    text-decoration: none;
  }
}

::v-deep .fc .fc-highlight {
  background: none;
}

::v-deep .fc-event-main {
  position: relative;
  font-size: 10px;
  box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.23), 0 3px 6px 0 rgba(0, 0, 0, 0.16);
  padding: 12px;

  &::before {
    position: absolute;
    font-size: 15px;
    display: flex;
    content: "=";
    height: 95%;
    width: 85%;
    align-items: flex-end;
    justify-content: center;
  }
}

::v-deep .fc-event {
  background: $nobel !important;
  border-color: $nobel !important;
}

//No reserved events
::v-deep .fc-event-draggable {
  background: $smalt !important;
  border-color: $smalt !important;
}

.header-badge {
  width: 96px;
  border-radius: 7px;
  background-color: rgba($harlequin, 0.1);
  font-size: 8px;
  color: $dim-gray;
}
</style>
